// src/components/modals/InviteTeammateModal.tsx
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { createClientOrgInvite } from '../../lib/clientOrganization';
import { useAuth } from '../../context/AuthProvider';
import {
  CLIENT_ORG_INVITE_CREATED_EVENT,
  type ClientOrgInviteCreatedEventDetail,
} from '../../lib/clientOrganizationEvents';

const Backdrop = styled.div`
  position: fixed; inset: 0; background: rgba(9,12,17,.72);
  display: grid; place-items: center; z-index: 1000;
`;
const Modal = styled.div`
  width: min(560px, 92vw);
  background: #12161b; border: 1px solid rgba(255,255,255,.1);
  border-radius: 14px; box-shadow: 0 20px 60px rgba(0,0,0,.5);
`;
const ModalHead = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  padding: 16px 18px; border-bottom: 1px solid rgba(255,255,255,.06);
  h3{ margin:0; color:#E8EAED; font-weight:900; }
`;
const ModalBody = styled.form`
  display:grid; gap:12px; padding:18px;
  label{ color:#cbd2e1; font-weight:700; font-size:.9rem; }
  input, select{
    width:100%; background:#0f1216; border:1px solid rgba(255,255,255,.14);
    color:#E8EAED; border-radius:10px; padding:.6rem .7rem;
  }
  small{ color:#9AA4B2; }
`;
const ModalFoot = styled.div`
  display:flex; justify-content:flex-end; gap:10px; padding:16px 18px;
  border-top: 1px solid rgba(255,255,255,.06);
`;
const CloseBtn = styled.button`
  appearance:none; border:0; background:transparent; color:#cbd2e1;
  font-size:20px; line-height:1; padding:.25rem .35rem; border-radius:8px; cursor:pointer;
  &:hover{ background: rgba(255,255,255,.06); color:#fff; }
`;
const Danger = styled.div` color:#ff8585; font-weight:700; `;
const Success = styled.div` color:#8DEBFF; font-weight:800; `;

export default function InviteTeammateModal({
  open, onClose, orgId,
}: { open: boolean; onClose: () => void; orgId: string }) {
  const { businessProfileComplete } = useAuth();
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'member'>('member');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string|null>(null);
  const [ok, setOk] = useState<string|null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setInviteRole('member');
      setErr(null);
      setOk(null);
      setBusy(false);
    }
    const onEsc = (e: KeyboardEvent) => { if (open && e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setOk(null);
    const emailTrim = email.trim().toLowerCase();
    if (!businessProfileComplete) {
      setErr('Complete and save required business profile fields before sending invites.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) { setErr('Enter a valid email'); return; }
    if (!orgId) { setErr('Organization id missing'); return; }
    try {
      setBusy(true);
      const invite = await createClientOrgInvite({ email: emailTrim, orgId, role: inviteRole });
      try {
        window.dispatchEvent(
          new CustomEvent<ClientOrgInviteCreatedEventDetail>(CLIENT_ORG_INVITE_CREATED_EVENT, {
            detail: { invite },
          }),
        );
      } catch {
        // no-op
      }
      setOk('Invite sent');
      setTimeout(onClose, 1200);
    } catch (e:any) {
      setErr(e?.message || 'Failed to send invite');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <Backdrop role="dialog" aria-modal="true" aria-labelledby="invite-title">
      <Modal>
        <ModalHead>
          <h3 id="invite-title">Invite organization member</h3>
          <CloseBtn onClick={onClose} aria-label="Close">x</CloseBtn>
        </ModalHead>
        <ModalBody onSubmit={submit}>
          <div>
            <label htmlFor="invite-email">Email</label>
            <input
              id="invite-email"
              type="email"
              placeholder="teammate@email.com"
              value={email}
              onChange={e=>setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="invite-role">Membership type</label>
            <select
              id="invite-role"
              value={inviteRole}
              onChange={(event) => setInviteRole(event.target.value === 'owner' ? 'owner' : 'member')}
            >
              <option value="member">Organization member</option>
              <option value="owner">Primary account holder</option>
            </select>
          </div>
          <small>
            {inviteRole === 'owner'
              ? 'Primary account holder invites transfer ownership when the invite is accepted.'
              : 'Invited collaborators join as organization members.'}
          </small>
          {!businessProfileComplete ? (
            <Danger>Complete and save required business profile fields before inviting teammates.</Danger>
          ) : null}
          {err && <Danger>{err}</Danger>}
          {ok  && <Success>{ok}</Success>}
          <ModalFoot>
            <button type="button" onClick={onClose} disabled={busy}
              style={{padding:'10px 14px',borderRadius:10,background:'transparent',border:'1px solid rgba(255,255,255,.14)',color:'#cbd2e1',fontWeight:800}}>
              Cancel
            </button>
            <button type="submit" disabled={busy || !businessProfileComplete}
              style={{padding:'10px 16px',borderRadius:10,background:'linear-gradient(90deg,#8DEBFF 0%,#F8C9A5 100%)',border:'1px solid rgba(0,0,0,.25)',color:'#111827',fontWeight:900}}>
              {busy ? 'Sending...' : 'Send invite'}
            </button>
          </ModalFoot>
        </ModalBody>
      </Modal>
    </Backdrop>,
    document.body
  );
}

